import { parseTripObject, type ParsedTrip } from "./trip-import";
import type { ShareRef, Stop } from "./types";

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? "";

export async function saveTripToWorker(
  stops: readonly Stop[],
  tripName?: string,
): Promise<ShareRef> {
  if (!WORKER_URL) throw new Error("VITE_WORKER_URL is not configured");
  const res = await fetch(`${WORKER_URL}/trips`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ stops, tripName }),
  });
  if (!res.ok) throw new Error(`Worker responded ${res.status}`);
  const { id, editToken } = (await res.json()) as { id: string; editToken: string };
  return { id, editToken };
}

/**
 * Overwrite an already-shared trip, keeping its link — and therefore every phone that
 * already opened it — pointed at the same id.
 *
 * Throws on 403 (the trip predates edit tokens, or the token is wrong) and 404 (expired).
 * Callers should treat both as "this link is no longer writable" and re-share.
 */
export async function updateTripOnWorker(
  share: ShareRef,
  stops: readonly Stop[],
  tripName?: string,
): Promise<void> {
  if (!WORKER_URL) throw new Error("VITE_WORKER_URL is not configured");
  const res = await fetch(`${WORKER_URL}/trips/${encodeURIComponent(share.id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", "X-Edit-Token": share.editToken },
    body: JSON.stringify({ stops, tripName }),
  });
  if (!res.ok) throw new Error(`Worker responded ${res.status}`);
}

export async function loadTripFromWorker(id: string): Promise<ParsedTrip | null> {
  if (!WORKER_URL) return null;
  const res = await fetch(`${WORKER_URL}/trips/${encodeURIComponent(id)}`);
  if (!res.ok) return null;
  try {
    return parseTripObject(await res.json());
  } catch {
    return null;
  }
}

const HASH_KEY = "trip";
const VERSION_GZIP = "g";
const VERSION_PLAIN = "j";

function hasCompression(): boolean {
  return typeof (globalThis as { CompressionStream?: unknown }).CompressionStream !== "undefined";
}

export async function encodeTripToHash(stops: readonly Stop[], tripName?: string): Promise<string> {
  const json = JSON.stringify({ stops, tripName });
  if (hasCompression()) {
    try {
      const bytes = await gzip(json);
      return VERSION_GZIP + bytesToBase64Url(bytes);
    } catch {
      // fall through to plain
    }
  }
  return VERSION_PLAIN + bytesToBase64Url(new TextEncoder().encode(json));
}

export async function decodeTripFromHash(hash: string): Promise<ParsedTrip | null> {
  const payload = readPayload(hash);
  if (!payload) return null;
  const version = payload[0];
  const body = payload.slice(1);
  let json: string;
  try {
    const bytes = base64UrlToBytes(body);
    if (version === VERSION_GZIP) {
      if (!hasCompression()) return null;
      json = new TextDecoder().decode(await gunzip(bytes));
    } else if (version === VERSION_PLAIN) {
      json = new TextDecoder().decode(bytes);
    } else {
      return null;
    }
  } catch {
    return null;
  }
  try {
    const parsed = JSON.parse(json);
    return parseTripObject(parsed);
  } catch {
    return null;
  }
}

export function buildWorkerShareUrl(id: string): string {
  const base = `${location.origin}${location.pathname}`;
  return `${base}?trip=${encodeURIComponent(id)}`;
}

export function clearTripParam(): void {
  const url = new URL(location.href);
  url.searchParams.delete("trip");
  history.replaceState(null, "", url.pathname + (url.search === "?" ? "" : url.search));
}

export function clearHash(): void {
  if (!location.hash) return;
  const url = location.pathname + location.search;
  history.replaceState(null, "", url);
}

export function buildShareUrl(encoded: string): string {
  const base = `${location.origin}${location.pathname}${location.search}`;
  return `${base}#${HASH_KEY}=${encoded}`;
}

function readPayload(hash: string): string | null {
  if (!hash) return null;
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(trimmed);
  const value = params.get(HASH_KEY);
  return value && value.length > 1 ? value : null;
}

async function gzip(input: string): Promise<Uint8Array> {
  const cs = new CompressionStream("gzip");
  const stream = new Response(input).body!.pipeThrough(cs);
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

async function gunzip(input: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream("gzip");
  const blob = new Blob([input as BlobPart]);
  const stream = blob.stream().pipeThrough(ds);
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlToBytes(input: string): Uint8Array {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const padding = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const bin = atob(padded + padding);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
